import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Loader2, BadgeCheck } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BookingCalendar from "@/components/BookingCalendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { FALLBACK_PACKAGES, FALLBACK_UPSELLS } from "@/lib/fallbackPackages";

const PACKAGE_DURATIONS = { decouverte: 60, premium: 360 };
const SUB_DURATIONS = [60, 120, 180];

const STEPS = [
    { id: 1, label: "Formule" },
    { id: 2, label: "Date & heure" },
    { id: 3, label: "Options" },
    { id: 4, label: "Vos infos" },
    { id: 5, label: "Paiement" },
];

export default function Reserver() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [packages, setPackages] = useState(FALLBACK_PACKAGES);
    const [upsells, setUpsells] = useState(FALLBACK_UPSELLS);
    const [loading, setLoading] = useState(false);
    const [abonnementMode, setAbonnementMode] = useState(searchParams.get("mode") === "session" ? "session" : ""); // "purchase" | "session"
    const [subDuration, setSubDuration] = useState(60);
    const [subStatus, setSubStatus] = useState(null); // statut abonné vérifié
    const [checkingSub, setCheckingSub] = useState(false);
    const [calRangeStart, setCalRangeStart] = useState(""); // semaine actuellement affichée

    const [form, setForm] = useState({
        package_id: searchParams.get("package") || "",
        date: "",
        time_slot: "",
        upsells: {}, // { id: qty }
        first_name: "",
        last_name: "",
        email: searchParams.get("email") || "",
        phone: "",
        company: "",
        project_description: "",
        payment_choice: "scalapay",
    });

    useEffect(() => {
        api.get("/packages")
            .then((r) => {
                if (Array.isArray(r.data?.packages) && r.data.packages.length > 0) setPackages(r.data.packages);
                if (Array.isArray(r.data?.upsells) && r.data.upsells.length > 0) setUpsells(r.data.upsells);
            })
            .catch(() => {
                // keep fallback
            });
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step]);

    const selectedPackage = packages.find((p) => p.id === form.package_id);
    const isAbo = form.package_id === "abonnement";
    const isSession = isAbo && abonnementMode === "session";
    const isAboPurchase = isAbo && abonnementMode === "purchase";

    // Flux d'étapes dynamique : l'achat d'abonnement n'a ni créneau ni options,
    // la session abonnée n'a ni options ni paiement.
    const FLOW = isSession
        ? [
              { id: 1, label: "Formule" },
              { id: 2, label: "Créneau" },
              { id: 4, label: "Vos infos" },
          ]
        : isAbo
        ? [
              { id: 1, label: "Formule" },
              { id: 4, label: "Vos infos" },
              { id: 5, label: "Paiement" },
          ]
        : STEPS;

    const flowIdx = FLOW.findIndex((s) => s.id === step);
    const goNext = () => {
        const next = FLOW[flowIdx + 1];
        if (next) setStep(next.id);
    };
    const goBack = () => {
        const prev = FLOW[flowIdx - 1];
        if (prev) setStep(prev.id);
    };

    // Recalcule le quota abonné pour la semaine affichée dans le calendrier
    useEffect(() => {
        if (!isSession || !subStatus?.active || !calRangeStart) return;
        api.get(`/subscribers/status?email=${encodeURIComponent(form.email)}&date=${calRangeStart}`)
            .then(({ data }) => setSubStatus(data))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calRangeStart]);

    const checkSubscriber = async () => {
        if (!/\S+@\S+\.\S+/.test(form.email)) {
            toast.error("Saisissez l'email utilisé lors de votre abonnement");
            return;
        }
        setCheckingSub(true);
        try {
            const { data } = await api.get(`/subscribers/status?email=${encodeURIComponent(form.email)}`);
            setSubStatus(data);
            if (!data.active) {
                toast.error("Aucun abonnement actif pour cet email");
            } else {
                toast.success(`Abonnement actif jusqu'au ${new Date(data.active_until).toLocaleDateString("fr-FR")} — ${data.week_remaining_min / 60}h restantes cette semaine`);
            }
        } catch {
            toast.error("Vérification impossible, réessayez");
        } finally {
            setCheckingSub(false);
        }
    };

    const upsellsTotal = Object.entries(form.upsells).reduce((sum, [id, qty]) => {
        const u = upsells.find((x) => x.id === id);
        return sum + (u ? u.price * qty : 0);
    }, 0);

    const total = (selectedPackage?.price || 0) + upsellsTotal;

    const toggleUpsell = (id) => {
        setForm((f) => {
            const next = { ...f.upsells };
            if (next[id]) delete next[id];
            else next[id] = 1;
            return { ...f, upsells: next };
        });
    };

    const updateUpsellQty = (id, delta) => {
        setForm((f) => {
            const next = { ...f.upsells };
            const cur = next[id] || 0;
            const newQty = Math.max(0, cur + delta);
            if (newQty === 0) delete next[id];
            else next[id] = newQty;
            return { ...f, upsells: next };
        });
    };

    const canNext = () => {
        if (step === 1) return !!form.package_id && (!isAbo || !!abonnementMode);
        if (step === 2) {
            if (isSession) return !!subStatus?.active && !!form.date && !!form.time_slot && subDuration <= (subStatus?.week_remaining_min ?? 0);
            return !!form.date && !!form.time_slot;
        }
        if (step === 3) return true;
        if (step === 4)
            return (
                form.first_name.trim() &&
                form.last_name.trim() &&
                /\S+@\S+\.\S+/.test(form.email) &&
                form.phone.trim().length >= 6
            );
        return true;
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload = {
                package_id: form.package_id,
                date: isAboPurchase ? "" : form.date,
                time_slot: isAboPurchase ? "" : form.time_slot,
                upsells: isAbo ? [] : Object.entries(form.upsells).map(([id, quantity]) => ({ id, quantity })),
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                phone: form.phone,
                company: form.company,
                project_description: form.project_description,
                payment_choice: isSession ? "pay_later" : form.payment_choice,
                duration_minutes: isSession ? subDuration : undefined,
                subscriber_session: isSession,
            };
            const { data: booking } = await api.post("/bookings", payload);

            if (isSession) {
                toast.success("Session abonné confirmée — à très vite au studio !");
                navigate(`/paiement/succes?booking_id=${booking.id}&mode=subscription`);
            } else if (form.payment_choice === "scalapay") {
                const origin = window.location.origin;
                const { data } = await api.post("/scalapay/session", {
                    booking_id: booking.id,
                    origin_url: origin,
                });
                window.location.href = data.url;
            } else {
                toast.success("Réservation enregistrée — nous revenons vers vous sous 24h.");
                navigate(`/paiement/succes?booking_id=${booking.id}&mode=pay_later`);
            }
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Une erreur est survenue");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Réserver une session — Studio Mon Séraphin"
                description="Réservez votre session au Studio Mon Séraphin en moins de 2 minutes. Ouvert 7j/7 de 08h00 à 20h00 à Villiers-sur-Marne (Paris Est) — RER A & RER E. Paiement en 3 fois sans frais avec Scalapay ou règlement sur place."
                path="/reserver"
                noindex
            />
            <Navbar />
            <main className="relative min-h-screen pt-32 pb-20 px-6 md:px-12" data-testid="reserver-page">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-[500px] bg-[radial-gradient(ellipse_at_top,rgba(245,183,84,0.08),transparent_70%)]" />

                <div className="relative max-w-6xl mx-auto">
                    <div className="max-w-2xl">
                        <span className="font-body text-xs uppercase tracking-[0.4em] text-amber-400/80">
                            Réservation
                        </span>
                        <h1 className="mt-3 font-display text-4xl md:text-5xl font-light tracking-tighter text-white leading-[1.05]">
                            Construisez votre session.
                        </h1>
                    </div>

                    {/* Stepper */}
                    <div className="mt-12 flex items-center gap-2 overflow-x-auto pb-2">
                        {FLOW.map((s, i) => (
                            <div key={s.id} className="flex items-center gap-2 shrink-0">
                                <div
                                    className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-body transition-all ${
                                        flowIdx === i
                                            ? "bg-amber-400 text-black border-amber-400"
                                            : flowIdx > i
                                            ? "bg-amber-400/10 text-amber-300 border-amber-400/30"
                                            : "text-zinc-500 border-white/10"
                                    }`}
                                >
                                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold bg-black/20">
                                        {flowIdx > i ? <Check className="w-3 h-3" /> : i + 1}
                                    </span>
                                    <span className="hidden sm:inline">{s.label}</span>
                                </div>
                                {i < FLOW.length - 1 && (
                                    <div className="w-6 h-px bg-white/10" />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 grid lg:grid-cols-[1fr_380px] gap-8">
                        {/* Main step content */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-10 min-h-[500px]">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={step}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {step === 1 && (
                                        <div data-testid="step-1-package">
                                            <h2 className="font-display text-2xl text-white font-medium">
                                                Choisissez votre formule
                                            </h2>
                                            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                                {packages.map((p) => (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => { setForm((f) => ({ ...f, package_id: p.id, date: "", time_slot: "" })); setAbonnementMode(""); setSubStatus(null); }}
                                                        className={`text-left rounded-2xl border p-5 transition-all ${
                                                            form.package_id === p.id
                                                                ? "border-amber-400 bg-amber-400/10"
                                                                : "border-white/10 bg-white/[0.02] hover:border-amber-400/40"
                                                        }`}
                                                        data-testid={`select-package-${p.id}`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <h3 className="font-display text-xl text-white font-medium">{p.name}</h3>
                                                                <p className="text-xs uppercase tracking-widest text-amber-400/80 mt-1">{p.tagline}</p>
                                                            </div>
                                                            <span className="font-display text-2xl text-white font-light">{p.price}€</span>
                                                        </div>
                                                        <p className="mt-3 text-sm text-zinc-400">{p.description}</p>
                                                        <p className="mt-2 text-xs text-amber-300/70 font-body">3 × {(p.price / 3).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€ sans frais</p>
                                                        <p className="mt-2 text-xs text-zinc-500 uppercase tracking-wider">{p.duration} · /{p.unit}</p>
                                                    </button>
                                                ))}
                                            </div>

                                            {isAbo && (
                                                <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="abonnement-mode">
                                                    <button
                                                        onClick={() => { setAbonnementMode("purchase"); setSubStatus(null); }}
                                                        className={`text-left rounded-2xl border p-5 transition-all ${
                                                            abonnementMode === "purchase"
                                                                ? "border-amber-400 bg-amber-400/10"
                                                                : "border-white/10 bg-white/[0.02] hover:border-amber-400/40"
                                                        }`}
                                                        data-testid="abonnement-mode-purchase"
                                                    >
                                                        <p className="font-display text-lg text-white font-medium">Je m'abonne</p>
                                                        <p className="mt-1 text-sm text-zinc-400">
                                                            350€/mois — activé dès le paiement, vous réservez ensuite vos 3h par semaine.
                                                        </p>
                                                    </button>
                                                    <button
                                                        onClick={() => { setAbonnementMode("session"); }}
                                                        className={`text-left rounded-2xl border p-5 transition-all ${
                                                            abonnementMode === "session"
                                                                ? "border-blue-400 bg-blue-400/10"
                                                                : "border-white/10 bg-white/[0.02] hover:border-blue-400/40"
                                                        }`}
                                                        data-testid="abonnement-mode-session"
                                                    >
                                                        <p className="font-display text-lg text-white font-medium">Je suis déjà abonné·e</p>
                                                        <p className="mt-1 text-sm text-zinc-400">
                                                            Réservez vos heures incluses — 3h par semaine, créneaux garantis.
                                                        </p>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {step === 2 && (
                                        <div data-testid="step-2-date">
                                            {isSession ? (
                                                <>
                                                    <h2 className="font-display text-2xl text-white font-medium">
                                                        Réservez vos heures d'abonné
                                                    </h2>
                                                    <div className="mt-5 rounded-2xl border border-blue-400/30 bg-blue-400/[0.05] p-5">
                                                        <Label className="text-zinc-400 uppercase text-xs tracking-widest">Email de votre abonnement *</Label>
                                                        <div className="mt-2 flex flex-col sm:flex-row gap-2">
                                                            <Input
                                                                type="email"
                                                                value={form.email}
                                                                onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setSubStatus(null); }}
                                                                placeholder="email utilise lors de l'achat"
                                                                className="bg-black/40 border-white/10 text-white"
                                                                data-testid="input-subscriber-email"
                                                            />
                                                            <Button
                                                                onClick={checkSubscriber}
                                                                disabled={checkingSub}
                                                                className="bg-blue-500 text-white hover:bg-blue-400 rounded-md shrink-0"
                                                                data-testid="check-subscriber-btn"
                                                            >
                                                                {checkingSub ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Vérification…</> : "Vérifier"}
                                                            </Button>
                                                        </div>
                                                        {subStatus?.active && (
                                                            <p className="mt-3 text-sm text-blue-300 flex items-center gap-2" data-testid="subscriber-active-badge">
                                                                <BadgeCheck className="w-4 h-4" />
                                                                Abonnement actif jusqu'au {new Date(subStatus.active_until).toLocaleDateString("fr-FR")} — {subStatus.week_remaining_min / 60}h restantes cette semaine.
                                                            </p>
                                                        )}
                                                        {subStatus && !subStatus.active && (
                                                            <p className="mt-3 text-sm text-red-300" data-testid="subscriber-inactive">
                                                                Aucun abonnement actif pour cet email. Revenez à l'étape 1 et choisissez « Je m'abonne ».
                                                            </p>
                                                        )}
                                                    </div>

                                                    {subStatus?.active && (
                                                        <>
                                                            <div className="mt-6">
                                                                <Label className="text-zinc-400 uppercase text-xs tracking-widest">Durée de la session</Label>
                                                                <div className="mt-2 flex gap-2">
                                                                    {SUB_DURATIONS.map((d) => {
                                                                        const allowed = d <= subStatus.week_remaining_min;
                                                                        return (
                                                                            <button
                                                                                key={d}
                                                                                onClick={() => allowed && (setSubDuration(d), setForm((f) => ({ ...f, time_slot: "" })))}
                                                                                disabled={!allowed}
                                                                                className={`rounded-xl border px-5 py-2.5 text-sm font-body transition-all ${
                                                                                    !allowed
                                                                                        ? "border-white/5 text-zinc-600 cursor-not-allowed"
                                                                                        : subDuration === d
                                                                                        ? "border-blue-400 bg-blue-400 text-white"
                                                                                        : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-blue-400/40"
                                                                                }`}
                                                                                data-testid={`sub-duration-${d}`}
                                                                            >
                                                                                {d / 60}h
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                            <div className="mt-6">
                                                                <BookingCalendar
                                                                    duration={subDuration}
                                                                    value={{ date: form.date, time_slot: form.time_slot }}
                                                                    onChange={({ date, time_slot }) => setForm((f) => ({ ...f, date, time_slot }))}
                                                                    onVisibleRangeChange={setCalRangeStart}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <h2 className="font-display text-2xl text-white font-medium">
                                                        Choisissez votre créneau
                                                    </h2>
                                                    <p className="mt-2 text-sm text-zinc-500">
                                                        {form.package_id === "premium"
                                                            ? "Session Premium de 6h d'un bloc, entre 08h00 et 20h00, 7j/7."
                                                            : "Session Découverte d'1h, entre 08h00 et 20h00, 7j/7."}{" "}
                                                        15 min de transition sont automatiquement réservées après chaque session.
                                                    </p>
                                                    <div className="mt-6">
                                                        <BookingCalendar
                                                            duration={PACKAGE_DURATIONS[form.package_id] || 60}
                                                            value={{ date: form.date, time_slot: form.time_slot }}
                                                            onChange={({ date, time_slot }) => setForm((f) => ({ ...f, date, time_slot }))}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {step === 3 && (
                                        <div data-testid="step-3-upsells">
                                            <h2 className="font-display text-2xl text-white font-medium">
                                                Ajoutez des options
                                            </h2>
                                            <p className="mt-2 text-sm text-zinc-500">Optionnel. Vous pouvez tout ajuster plus tard.</p>
                                            <div className="mt-6 space-y-3">
                                                {upsells.map((u) => {
                                                    const active = !!form.upsells[u.id];
                                                    const qty = form.upsells[u.id] || 0;
                                                    return (
                                                        <div
                                                            key={u.id}
                                                            className={`rounded-2xl border p-5 flex items-center justify-between gap-4 transition-all ${
                                                                active ? "border-amber-400/50 bg-amber-400/[0.05]" : "border-white/10 bg-white/[0.02]"
                                                            }`}
                                                            data-testid={`upsell-row-${u.id}`}
                                                        >
                                                            <div className="flex-1">
                                                                <p className="font-display text-lg text-white font-medium">{u.name}</p>
                                                                <p className="text-sm text-zinc-500">{u.description}</p>
                                                            </div>
                                                            <p className="font-display text-lg text-amber-400">+{u.price}€</p>
                                                            {active ? (
                                                                <div className="flex items-center gap-2">
                                                                    <button onClick={() => updateUpsellQty(u.id, -1)} className="w-8 h-8 rounded-full border border-white/20 text-white hover:border-amber-400" data-testid={`upsell-minus-${u.id}`}>-</button>
                                                                    <span className="w-8 text-center text-white">{qty}</span>
                                                                    <button onClick={() => updateUpsellQty(u.id, +1)} className="w-8 h-8 rounded-full border border-white/20 text-white hover:border-amber-400" data-testid={`upsell-plus-${u.id}`}>+</button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => toggleUpsell(u.id)}
                                                                    className="rounded-full border border-white/20 px-4 py-1.5 text-sm text-white hover:border-amber-400 hover:text-amber-300 transition"
                                                                    data-testid={`upsell-add-${u.id}`}
                                                                >
                                                                    Ajouter
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {step === 4 && (
                                        <div data-testid="step-4-infos">
                                            <h2 className="font-display text-2xl text-white font-medium">Vos informations</h2>
                                            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="text-zinc-400 uppercase text-xs tracking-widest">Prénom *</Label>
                                                    <Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="mt-2 bg-black/40 border-white/10 text-white" data-testid="input-first-name" />
                                                </div>
                                                <div>
                                                    <Label className="text-zinc-400 uppercase text-xs tracking-widest">Nom *</Label>
                                                    <Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="mt-2 bg-black/40 border-white/10 text-white" data-testid="input-last-name" />
                                                </div>
                                                <div>
                                                    <Label className="text-zinc-400 uppercase text-xs tracking-widest">Email *</Label>
                                                    <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} readOnly={isSession} className={`mt-2 bg-black/40 border-white/10 text-white ${isSession ? "opacity-60 cursor-not-allowed" : ""}`} data-testid="input-email" />
                                                    {isSession && <p className="mt-1 text-[11px] text-blue-300/80">Email vérifié de votre abonnement.</p>}
                                                </div>
                                                <div>
                                                    <Label className="text-zinc-400 uppercase text-xs tracking-widest">Téléphone *</Label>
                                                    <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-2 bg-black/40 border-white/10 text-white" data-testid="input-phone" />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <Label className="text-zinc-400 uppercase text-xs tracking-widest">Entreprise / Projet</Label>
                                                    <Input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="mt-2 bg-black/40 border-white/10 text-white" data-testid="input-company" />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <Label className="text-zinc-400 uppercase text-xs tracking-widest">Parlez-nous de votre podcast</Label>
                                                    <Textarea rows={4} value={form.project_description} onChange={(e) => setForm((f) => ({ ...f, project_description: e.target.value }))} className="mt-2 bg-black/40 border-white/10 text-white" data-testid="input-description" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {step === 5 && (
                                        <div data-testid="step-5-payment">
                                            <h2 className="font-display text-2xl text-white font-medium">Paiement</h2>
                                            <p className="mt-2 text-sm text-zinc-500">Choisissez comment vous souhaitez procéder.</p>
                                            <RadioGroup
                                                value={form.payment_choice}
                                                onValueChange={(v) => setForm((f) => ({ ...f, payment_choice: v }))}
                                                className="mt-6 space-y-3"
                                            >
                                                <label
                                                    className={`flex items-start gap-4 rounded-2xl border p-5 cursor-pointer transition-all ${
                                                        form.payment_choice === "scalapay"
                                                            ? "border-pink-300/60 bg-pink-300/[0.08]"
                                                            : "border-white/10 bg-white/[0.02] hover:border-pink-300/30"
                                                    }`}
                                                    data-testid="payment-choice-scalapay"
                                                >
                                                    <RadioGroupItem value="scalapay" id="pay-scalapay" className="mt-1 border-pink-300 text-pink-300" />
                                                    <div>
                                                        <p className="font-display text-lg text-white font-medium">
                                                            Payer en 3 fois avec <span className="text-pink-300">Scalapay</span>
                                                        </p>
                                                        <p className="text-sm text-zinc-500 mt-1">
                                                            3 × {(total / 3).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€ sans frais. Vérification en 1 min, confirmation immédiate.
                                                        </p>
                                                        <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest">
                                                            <span className="rounded border border-pink-300/40 bg-pink-300/[0.06] px-2 py-0.5 text-pink-200">Scalapay · 3×</span>
                                                        </div>
                                                    </div>
                                                </label>

                                                <label
                                                    className={`flex items-start gap-4 rounded-2xl border p-5 cursor-pointer transition-all ${
                                                        form.payment_choice === "pay_later"
                                                            ? "border-amber-400/60 bg-amber-400/[0.06]"
                                                            : "border-white/10 bg-white/[0.02] hover:border-amber-400/30"
                                                    }`}
                                                    data-testid="payment-choice-later"
                                                >
                                                    <RadioGroupItem value="pay_later" id="pay-later" className="mt-1 border-amber-400 text-amber-400" />
                                                    <div>
                                                        <p className="font-display text-lg text-white font-medium">Payer plus tard</p>
                                                        <p className="text-sm text-zinc-500 mt-1">Réservez sans payer. Nous revenons vers vous sous 24h pour confirmer et arrêter les modalités de paiement.</p>
                                                    </div>
                                                </label>
                                            </RadioGroup>
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>

                            <div className="mt-10 flex items-center justify-between">
                                <Button
                                    variant="ghost"
                                    onClick={goBack}
                                    disabled={flowIdx === 0 || loading}
                                    className="text-zinc-400 hover:text-white hover:bg-white/5"
                                    data-testid="booking-back-btn"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Retour
                                </Button>
                                {flowIdx < FLOW.length - 1 ? (
                                    <Button
                                        onClick={goNext}
                                        disabled={!canNext()}
                                        className="bg-amber-400 text-black hover:bg-amber-300 rounded-full px-6"
                                        data-testid="booking-next-btn"
                                    >
                                        Continuer <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className="bg-amber-400 text-black hover:bg-amber-300 rounded-full px-6"
                                        data-testid="booking-submit-btn"
                                    >
                                        {loading ? (
                                            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Traitement…</>
                                        ) : form.payment_choice === "scalapay" ? (
                                            "Continuer avec Scalapay"
                                        ) : (
                                            "Confirmer la réservation"
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Summary sidebar */}
                        <aside className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8 h-fit sticky top-28" data-testid="booking-summary">
                            <h3 className="font-display text-lg text-white font-medium">Récapitulatif</h3>
                            <div className="mt-4 space-y-4 text-sm">
                                {selectedPackage ? (
                                    <div className="flex justify-between text-zinc-300">
                                        <span>{isSession ? "Session abonné" : selectedPackage.name}{isSession ? ` · ${subDuration / 60}h` : ""}</span>
                                        <span className="text-white">{isSession ? "Inclus" : `${selectedPackage.price}€`}</span>
                                    </div>
                                ) : (
                                    <p className="text-zinc-500">Sélectionnez une formule…</p>
                                )}

                                {form.date && (
                                    <div className="flex justify-between text-zinc-500 text-xs">
                                        <span>
                                            {format(new Date(`${form.date}T12:00:00`), "d MMM yyyy", { locale: fr })}
                                            {form.time_slot && ` · ${form.time_slot} → ${(() => { const [h, m] = form.time_slot.split(":").map(Number); const e = h * 60 + m + (isSession ? subDuration : PACKAGE_DURATIONS[form.package_id] || 60); return `${String(Math.floor(e / 60)).padStart(2, "0")}:${String(e % 60).padStart(2, "0")}`; })()}`}
                                        </span>
                                    </div>
                                )}

                                {isSession && subStatus?.active && (
                                    <p className="text-xs text-blue-300/80 font-body" data-testid="summary-sub-remaining">
                                        {(subStatus.week_remaining_min - (form.time_slot ? subDuration : 0)) / 60}h restantes cette semaine après cette session.
                                    </p>
                                )}

                                {Object.entries(form.upsells).map(([id, qty]) => {
                                    const u = upsells.find((x) => x.id === id);
                                    if (!u) return null;
                                    return (
                                        <div key={id} className="flex justify-between text-zinc-400">
                                            <span>{u.name} × {qty}</span>
                                            <span className="text-white">{(u.price * qty).toFixed(0)}€</span>
                                        </div>
                                    );
                                })}

                                <div className="h-px bg-white/10" />
                                <div className="flex justify-between items-baseline">
                                    <span className="text-zinc-400 uppercase text-xs tracking-widest">Total</span>
                                    <span className="font-display text-3xl text-amber-400 font-light">{isSession ? "0€" : `${total.toFixed(0)}€`}</span>
                                </div>
                                {!isSession && total > 0 && (
                                    <p className="text-xs text-amber-300/80 font-body -mt-1" data-testid="summary-installments">
                                        soit 3 × {(total / 3).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€ sans frais avec Scalapay
                                    </p>
                                )}
                                <p className="text-[11px] text-zinc-600">
                                    {isSession
                                        ? "Session incluse dans votre abonnement mensuel (3h/semaine)."
                                        : "TTC. Pour les abonnements mensuels, le total correspond au premier mois."}
                                </p>
                            </div>
                        </aside>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}
